/**
 * Voice AI — IVR Extensions Tab
 * Extension directory: manage agent extension numbers.
 * Self-loading: fetches own data on connectedCallback.
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { ivrStyles } from '../../styles/ivr-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { ivrService } from '../../services/ivr-service.js';
import { confirmService } from '../../services/confirm-service.js';
import '../voice-modal.js';

export class IvrExtensionsTab extends LitElement {
  static properties = {
    agents: { type: Array },
    _extensions: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _showModal: { type: Boolean, state: true },
    _editing: { type: Object, state: true },
  };

  static styles = [designSystem, componentStyles, ivrStyles];

  constructor() {
    super();
    this.agents = [];
    this._extensions = [];
    this._loading = true;
    this._showModal = false;
    this._editing = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadExtensions();
  }

  async _loadExtensions() {
    this._loading = true;
    try {
      const data = await ivrService.listExtensions();
      if (data.success) {
        this._extensions = data.extensions || [];
      }
    } catch (err) {
      console.error('[IvrExtensionsTab] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _toast(message) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message } }));
  }

  _openAdd() {
    this._editing = {
      extension: '',
      agent_id: '',
      label: '',
      status: 'active',
    };
    this._showModal = true;
  }

  _openEdit(ext) {
    this._editing = { ...ext };
    this._showModal = true;
  }

  _closeModal() {
    this._showModal = false;
    this._editing = null;
  }

  async _saveExtension() {
    const root = this.shadowRoot;
    const ext = {
      ...this._editing,
      extension: root.querySelector('#ext-number')?.value || '',
      agent_id: parseInt(root.querySelector('#ext-agent')?.value) || null,
      label: root.querySelector('#ext-label')?.value || '',
      status: root.querySelector('#ext-status')?.value || 'active',
    };

    // Validate
    if (!/^\d{2,6}$/.test(ext.extension)) {
      this._toast('Extension must be 2-6 digits');
      return;
    }
    if (!ext.agent_id) {
      this._toast('Please select an agent');
      return;
    }

    try {
      let data;
      if (ext.id) {
        data = await ivrService.updateExtension(ext.id, ext);
      } else {
        data = await ivrService.createExtension(ext);
      }
      if (data.success) {
        this._closeModal();
        this._loadExtensions();
        this._toast(ext.id ? 'Extension updated' : 'Extension created');
      } else {
        this._toast('Save failed: ' + (data.error || 'Unknown'));
      }
    } catch (err) {
      this._toast('Save failed: ' + err.message);
    }
  }

  async _confirmDelete(id) {
    const confirmed = await confirmService.show({
      title: 'Delete Extension',
      icon: 'fa-triangle-exclamation',
      description: 'This extension will no longer be reachable.',
    });
    if (!confirmed) return;
    try {
      const data = await ivrService.deleteExtension(id);
      if (data.success) {
        this._loadExtensions();
        this._toast('Extension deleted');
      }
    } catch (err) {
      this._toast('Delete failed: ' + err.message);
    }
  }

  render() {
    return html`
      ${iconStyles}

      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-phone-office"></i> Extension Directory</div>
            <div class="section-description">Assign extension numbers to agents. Callers can dial an extension to reach a specific agent.</div>
          </div>
          <button class="btn btn-primary btn-sm" @click=${this._openAdd}>
            <i class="fa-sharp fa-light fa-plus"></i> Add Extension
          </button>
        </div>

        ${this._loading ? html`
          <table class="ext-table">
            <thead>
              <tr><th>Ext #</th><th>Agent</th><th>Label</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              ${[1, 2, 3].map(() => html`
                <tr>
                  <td><div class="skeleton-line" style="width:40px;height:14px;"></div></td>
                  <td><div class="skeleton-line" style="width:100px;height:14px;"></div></td>
                  <td><div class="skeleton-line" style="width:80px;height:14px;"></div></td>
                  <td><div class="skeleton-line" style="width:50px;height:14px;"></div></td>
                  <td></td>
                </tr>
              `)}
            </tbody>
          </table>
        ` : this._extensions.length === 0 ? html`
          <div class="empty-state" style="padding:var(--space-8);">
            <div class="empty-state-icon" style="font-size:var(--text-3xl);"><i class="fa-sharp fa-light fa-phone-office"></i></div>
            <div class="empty-state-title" style="font-size:var(--text-base);">No extensions</div>
            <div class="empty-state-description">Create extensions so callers can dial directly to specific agents.</div>
          </div>
        ` : html`
          <table class="ext-table">
            <thead>
              <tr><th>Ext #</th><th>Agent</th><th>Label</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              ${this._extensions.map(ext => html`
                <tr>
                  <td><span class="ext-number">${ext.extension}</span></td>
                  <td>${ext.agent_name || '—'}</td>
                  <td>${ext.label || '—'}</td>
                  <td>
                    <span class="badge ${ext.status === 'active' ? 'badge-success' : 'badge-neutral'}" style="font-size:9px;">
                      ${ext.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div class="option-actions">
                      <button title="Edit" @click=${() => this._openEdit(ext)}>
                        <i class="fa-sharp fa-light fa-pen-to-square"></i>
                      </button>
                      <button class="danger" title="Delete" @click=${() => this._confirmDelete(ext.id)}>
                        <i class="fa-sharp fa-light fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        `}
      </div>

      <!-- Extension Modal -->
      <voice-modal ?open=${this._showModal} title="${this._editing?.id ? 'Edit Extension' : 'Add Extension'}" width="440px"
        @modal-close=${this._closeModal}>
        <div style="padding:var(--space-6, 24px);">
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Extension Number</label>
            <input type="text" class="form-input" id="ext-number"
              .value=${this._editing?.extension || ''}
              placeholder="e.g. 101"
              pattern="\\d{2,6}"
              maxlength="6">
            <div class="form-hint">2-6 digits</div>
          </div>
          <div class="form-group">
            <label class="form-label">Agent</label>
            <select class="form-select" id="ext-agent">
              <option value="">-- Select Agent --</option>
              ${(this.agents || []).map(a => html`
                <option value="${a.id}" ?selected=${this._editing?.agent_id === a.id}>${a.name}</option>
              `)}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Label</label>
            <input type="text" class="form-input" id="ext-label"
              .value=${this._editing?.label || ''}
              placeholder="e.g. Front Desk">
          </div>
          <div class="form-group">
            <label class="form-label">Status</label>
            <select class="form-select" id="ext-status">
              <option value="active" ?selected=${this._editing?.status === 'active'}>Active</option>
              <option value="inactive" ?selected=${this._editing?.status === 'inactive'}>Inactive</option>
            </select>
          </div>
        </div>
        </div>
        <div slot="footer">
          <button class="btn btn-secondary" @click=${this._closeModal}>Cancel</button>
          <button class="btn btn-primary" @click=${this._saveExtension}>
            ${this._editing?.id ? 'Update' : 'Create'}
          </button>
        </div>
      </voice-modal>

    `;
  }
}

customElements.define('ivr-extensions-tab', IvrExtensionsTab);
